import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { ApiKeysListPage } from "../../../components/dashboard/api-keys";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  createApiKeyAction,
  deleteApiKeyAction,
  getApiKeysView,
} from "@/features/api-keys/server-fns";
import { apiKeysQueryOptions } from "@/features/api-keys/queries";
import { capturePostHogEvent } from "@/lib/posthog-client";

export const Route = createFileRoute("/dashboard/api-keys/")({
  head: () => ({
    meta: [{ title: "API Keys - Bavimail" }],
  }),
  staleTime: 30_000,
  preloadStaleTime: 30_000,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(apiKeysQueryOptions());
  },
  component: ApiKeysRoute,
});

function ApiKeysRoute() {
  const queryClient = useQueryClient();
  const { data: keys } = useSuspenseQuery(apiKeysQueryOptions());
  const apiKeysAnimationStage = useOneTimePageAnimation("dashboard-api-keys-list");

  const createMutation = useMutation({
    mutationFn: async (name: string) => await createApiKeyAction({ data: { name } }),
    onSuccess: (createdKey) => {
      capturePostHogEvent("api_key_created");
      queryClient.setQueryData<Awaited<ReturnType<typeof getApiKeysView>>>(
        ["apiKeys"],
        (current) => {
          if (!current) return [createdKey];
          return [createdKey, ...current.filter((key) => key.id !== createdKey.id)];
        },
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (keyId: string) => await deleteApiKeyAction({ data: { keyId } }),
    onMutate: async (keyId) => {
      await queryClient.cancelQueries({ queryKey: ["apiKeys"] });
      const previous = queryClient.getQueryData<Awaited<ReturnType<typeof getApiKeysView>>>([
        "apiKeys",
      ]);
      queryClient.setQueryData<Awaited<ReturnType<typeof getApiKeysView>>>(
        ["apiKeys"],
        (current) => current?.filter((key) => key.id !== keyId) ?? current,
      );
      return { previous };
    },
    onError: (_error, _keyId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["apiKeys"], context.previous);
      }
    },
  });

  return (
    <ApiKeysListPage
      keys={keys}
      apiKeysAnimationStage={apiKeysAnimationStage}
      onCreate={async (name: string) => await createMutation.mutateAsync(name)}
      onDelete={async (keyId: string) => {
        await deleteMutation.mutateAsync(keyId);
      }}
    />
  );
}
